import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule, DOCUMENT } from '@angular/common'; // Added DOCUMENT
import { Component, effect, inject, input, numberAttribute, signal } from '@angular/core'; // Removed unused DEBUG
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'; // Keep FormControl
import { ActivatedRoute, Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowRight,
  lucideBadgeCheck,
  lucideDownload,
  lucideExternalLink,
  lucideGithub,
  lucideGlobe,
  lucidePlug,
  lucideRefreshCw,
  lucideSearch,
  lucideStar,
  lucideTriangleAlert,
  lucideX,
} from '@ng-icons/lucide';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmBadgeDirective } from '@spartan-ng/ui-badge-helm';
import { HlmButtonDirective } from '@spartan-ng/ui-button-helm';
import { HlmIconDirective } from '@spartan-ng/ui-icon-helm'; // Re-added HlmIconDirective and HlmIconComponent
import { HlmInputDirective } from '@spartan-ng/ui-input-helm';
import { HlmLabelDirective } from '@spartan-ng/ui-label-helm';
import { HlmNumberedPaginationComponent } from '@spartan-ng/ui-pagination-helm';
import { HlmSelectImports } from '@spartan-ng/ui-select-helm';
import { HlmTooltipImports } from '@spartan-ng/ui-tooltip-helm';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ErrorMessageComponent } from '../../components/error-message/error-message.component';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { McpClient, McpPaginatedResponse } from '../../models/mcp.model';
import { HighlightSearchPipe } from '../../pipes/highlight-search.pipe';
import { McpService } from '../../services/mcp.service';
import { SEOService } from '../../services/seo.service';
import { StructuredDataService } from '../../services/structured-data.service';
import { ItemListSchema, ListItemSchema, SoftwareSourceCodeSchema } from '../../types/structured-data.interfaces'; // Updated import path
import { formatNumberShorthand } from '../../utils/number-formatting.utils';

@Component({
  selector: 'app-mcp-clients',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HlmNumberedPaginationComponent,
    HlmButtonDirective,
    HlmInputDirective,
    HlmLabelDirective,
    BrnSelectImports,
    HlmSelectImports,
    HlmTooltipImports,
    HlmBadgeDirective,
    HlmIconDirective,
    LoadingSpinnerComponent,
    ErrorMessageComponent,
    NgIcon,
    HlmTooltipImports,
    HighlightSearchPipe,
  ],
  templateUrl: './mcp-clients.component.html',
  styleUrl: './mcp-clients.component.scss',
  providers: [
    provideIcons({
      lucideTriangleAlert,
      lucideRefreshCw,
      lucideStar,
      lucideDownload,
      lucideGithub,
      lucideSearch,
      lucideArrowRight,
      lucideBadgeCheck,
      lucideGlobe,
      lucideExternalLink,
      lucidePlug,
      lucideX,
    }),
  ],
})
export class McpClientsComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  protected readonly mcpService = inject(McpService);
  private readonly seoService = inject(SEOService);
  private readonly structuredDataService = inject(StructuredDataService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private document = inject(DOCUMENT);

  readonly page = input(1, { transform: numberAttribute });
  readonly pageSize = input(12, { transform: numberAttribute });
  readonly sort = input<'stars' | 'alphabetical'>('stars');
  readonly search = input<string | undefined>(undefined);
  readonly featured = input<'all' | 'featured'>('all');

  protected readonly clients = signal<McpClient[]>([]);
  protected readonly totalItems = signal(0);
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly featuredFilter = signal<'all' | 'featured'>('all');

  protected readonly responsiveMaxSize = signal(7); // Default maxSize

  protected readonly searchInputControl = new FormControl<string | undefined>(undefined);
  protected readonly sortControl = new FormControl<'stars' | 'alphabetical'>('stars');

  protected readonly sortOptions: { value: 'stars' | 'alphabetical'; label: string }[] = [
    { value: 'stars', label: 'Stars (high to low)' },
    { value: 'alphabetical', label: 'Name (a-z)' },
  ];

  protected readonly formatNumber = formatNumberShorthand;

  constructor() {
    const currentParams = this.route.snapshot.queryParams;
    const paramsToSet: Record<string, string | number> = {};
    let performNavigation = false;

    // Normalize 'page' parameter
    const expectedPage = this.page(); // Default 1
    if (!currentParams['page'] || isNaN(Number(currentParams['page']))) {
      paramsToSet['page'] = expectedPage;
      performNavigation = true;
    }

    // Normalize 'pageSize' parameter
    const expectedPageSize = this.pageSize(); // Default 12
    if (!currentParams['pageSize'] || isNaN(Number(currentParams['pageSize']))) {
      paramsToSet['pageSize'] = expectedPageSize;
      performNavigation = true;
    }

    // Ensure sort and search also have defaults if missing
    const expectedSort = this.sort(); // Default 'stars'
    if (!currentParams['sort']) {
      paramsToSet['sort'] = expectedSort;
      performNavigation = true;
    } else {
      // Validate if the sort value from URL is one of the allowed values
      const allowedSortValues: Array<'stars' | 'alphabetical'> = ['stars', 'alphabetical'];
      if (!allowedSortValues.includes(currentParams['sort'])) {
        paramsToSet['sort'] = expectedSort; // Fallback to default if invalid
        performNavigation = true;
      }
    }

    const expectedSearch = this.search(); // Default undefined
    if (typeof currentParams['search'] === 'undefined' && expectedSearch !== undefined) {
      // Only set if there's a default search defined and it's missing from URL
      // For current setup, search defaults to undefined, so this block might not be strictly needed
      // but good for consistency if default search changes.
      paramsToSet['search'] = expectedSearch as string; // Cast if you are sure expectedSearch is string here
      performNavigation = true;
    } else if (typeof currentParams['search'] === 'undefined' && expectedSearch === undefined) {
      // Ensure 'search' param is at least empty string if not present and default is undefined
      // This depends on whether the backend/service expects empty string vs no param
      // For now, let's ensure it's explicitly handled if it needs to be an empty string.
      // If undefined is fine, this part can be skipped.
    }

    // Check for featured filter in URL params
    const expectedFeatured = this.featured(); // Default 'all'
    if (!currentParams['featured']) {
      paramsToSet['featured'] = expectedFeatured;
      performNavigation = true;
    } else if (currentParams['featured'] !== 'all' && currentParams['featured'] !== 'featured') {
      paramsToSet['featured'] = expectedFeatured; // Fallback to default if invalid
      performNavigation = true;
    }

    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .pipe(takeUntilDestroyed())
      .subscribe(result => {
        if (result.breakpoints[Breakpoints.XSmall]) {
          this.responsiveMaxSize.set(3);
        } else if (result.breakpoints[Breakpoints.Small]) {
          this.responsiveMaxSize.set(5);
        } else {
          this.responsiveMaxSize.set(7); // Default for larger screens
        }
      });

    if (performNavigation) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: paramsToSet,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    effect(() => {
      const currentPage = this.page();
      const currentPageSize = this.pageSize();
      const currentSort = this.sort();
      const currentSearch = this.search();
      const currentFeatured = this.featured();

      // Sync featuredFilter signal with URL parameter
      if (this.featuredFilter() !== currentFeatured) {
        this.featuredFilter.set(currentFeatured);
      }

      this.loadClients(currentPage, currentPageSize, currentSort, currentSearch);

      // Sync controls with URL params
      if (this.searchInputControl.value !== currentSearch) {
        this.searchInputControl.setValue(currentSearch, { emitEvent: false });
      }
      if (this.sortControl.value !== currentSort) {
        this.sortControl.setValue(currentSort, { emitEvent: false });
      }
    });

    this.setSeoAndStructuredData(); // Call method to set SEO and structured data

    // Subscribe to searchInputControl value changes for debouncing
    this.searchInputControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(value => {
        // Only perform search if more than 1 character or empty/undefined (to clear search)
        const searchValue = value || '';
        if (searchValue.length > 1 || searchValue.length === 0) {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { search: value || undefined, page: 1 }, // Reset to page 1 on new search
            queryParamsHandling: 'merge',
          });
        }
      });

    this.sortControl.valueChanges.subscribe(value => {
      this.onSortChange(value as 'stars' | 'alphabetical');
    });
  }

  protected loadClients(page: number, pageSize: number, sort: 'stars' | 'alphabetical', search?: string) {
    this.loading.set(true);
    this.error.set(null);

    // Use the featuredFilter signal to determine filtering
    const featuredValue = this.featuredFilter();

    this.mcpService.getMcpClients(page, pageSize, sort, search, featuredValue).subscribe({
      next: (response: McpPaginatedResponse<McpClient>) => {
        this.clients.set(response.items);
        this.totalItems.set(response.total);
        this.loading.set(false);
        this.setSeoAndStructuredData();
      },
      error: err => {
        this.error.set('Failed to load MCP clients. Please try again later.');
        console.error(err);
        this.loading.set(false);
      },
    });
  }

  // Method to set SEO and structured data
  private setSeoAndStructuredData() {
    const canonicalUrl = `${environment.canonicalOrigin}/mcp-clients`;
    const pageTitle = 'MCP Clients | Summerry';
    const pageDescription =
      'Discover and explore a curated list of Model Context Protocol (MCP) clients. Find tools and applications that leverage MCP for enhanced AI interactions.';

    this.seoService.updateTags({
      title: pageTitle,
      description: pageDescription,
      keywords: 'MCP clients, Model Context Protocol, AI tools, MCP ecosystem, AI applications, client software',
      type: 'website',
      url: canonicalUrl,
    });

    this.structuredDataService.clearSchema();
    this.structuredDataService.addBaseNodes();
    this.structuredDataService.addBreadcrumb([{ name: 'Home', item: origin }, { name: 'MCP Clients' }]);

    const clientItems = this.clients();

    const itemListElements: ListItemSchema[] = clientItems.map((client, index) => {
      const canonicalProductUrl = this.findGithubUrl(client.urls) || client.urls?.[0] || `${canonicalUrl}/${client.id}`;
      const productId = canonicalProductUrl;

      const productItem: SoftwareSourceCodeSchema = {
        '@type': 'SoftwareSourceCode',
        '@id': productId,
        name: client.name,
        description: client.summary || 'A client compatible with the Model Context Protocol.',
        url: canonicalProductUrl,
        codeRepository: canonicalProductUrl,
      };

      return {
        '@type': 'ListItem',
        position: index + 1,
        item: productItem,
      };
    });

    const itemList: ItemListSchema = {
      '@type': 'ItemList',
      itemListElement: itemListElements,
      numberOfItems: itemListElements.length,
    };

    this.structuredDataService.addCollectionPage({
      name: 'MCP Clients', // This is pageTitle for clients
      description: 'A collection of MCP clients.', // This is pageDescription for clients
      mainEntity: itemList,
      searchPath: '/mcp-clients', // Added searchPath
    });

    this.structuredDataService.generateAndInjectSchema();
  }

  protected onPageChange(newPage: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page: newPage },
      queryParamsHandling: 'merge',
    });
  }

  protected onPageSizeChange(newPageSize: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { pageSize: newPageSize, page: 1 }, // Reset to page 1 on size change
      queryParamsHandling: 'merge',
    });
  }

  protected onSortChange(newSort: 'stars' | 'alphabetical'): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sort: newSort, page: 1 }, // Reset to page 1 on sort change
      queryParamsHandling: 'merge',
    });
  }

  setFeaturedFilter(filter: 'all' | 'featured'): void {
    this.featuredFilter.set(filter);

    // Update the URL with the new featured filter
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { featured: filter, page: 1 }, // Reset to page 1 on filter change
      queryParamsHandling: 'merge',
    });
  }

  clearSearch() {
    this.searchInputControl.setValue('');
  }

  findGithubUrl(urls: string[] | undefined): string | undefined {
    if (!urls) {
      return undefined;
    }
    return urls.find(url => url.includes('github.com'));
  }

  getDomainFromUrl(url: string | undefined): string {
    if (!url) {
      return 'N/A';
    }
    try {
      const hostname = new URL(url).hostname;
      // Remove www. if present
      return hostname.startsWith('www.') ? hostname.substring(4) : hostname;
    } catch {
      // console.error('Error parsing URL for domain:', e); // Optional: for debugging
      return 'Invalid URL';
    }
  }

  getRepoOwner(githubUrl: string | undefined): string {
    if (!githubUrl) {
      return 'N/A';
    }
    try {
      const match = githubUrl.match(/github\.com\/([^/]+)\/[^/]+/);
      return match && match[1] ? match[1] : 'Unknown Owner';
    } catch {
      return 'Error Parsing Owner';
    }
  }

  getRepoNameFromUrl(githubUrl: string | undefined): string {
    if (!githubUrl) {
      return 'N/A';
    }
    try {
      const match = githubUrl.match(/github\.com\/[^/]+\/([^/]+)/);
      // Remove .git if present
      return match && match[1] ? match[1].replace(/\.git$/, '') : 'Unknown Repo';
    } catch {
      return 'Error Parsing Repo';
    }
  }
}
